import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import styles from './LoginPage.module.css';

const GENERIC_ERROR = 'That email or password is not right.';
const RATE_LIMIT_ERROR = 'Too many attempts. Wait a few minutes and try again.';

/**
 * A Map, deliberately, not an object literal. An object literal inherits from
 * Object.prototype, so `map['constructor']` returns a Function and
 * `map['__proto__']` an object — both truthy, so `?? generic` never fires and
 * React is handed a non-string child. TypeScript cannot see it: the index
 * signature says `string | undefined` while the runtime disagrees. The code is
 * attacker-supplied via /login?error=<code>, so those keys are reachable.
 */
const GOOGLE_ERROR_MAP = new Map<string, string>([
  ['access_denied', 'You cancelled Google sign-in.'],
  ['not_registered', 'That Google account is not authorised for this app.'],
]);

const GOOGLE_ERROR_GENERIC = 'Google sign-in failed. Try again, or use your password.';

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * Whether this deployment allows public signup. Starts false so the link is
   * never shown before the server has said yes — offering "create an account"
   * on a site with registration closed is a dead end, and defaulting to hidden
   * means a failed check degrades to the current behaviour rather than to a
   * broken promise.
   */
  const [registrationOpen, setRegistrationOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    apiGet<{ enabled: boolean }>('/auth/registration')
      .then((result) => {
        if (!cancelled) {
          setRegistrationOpen(result.enabled === true);
        }
      })
      // Swallowed on purpose: this is decoration on a screen whose actual job
      // is signing in, and an offline or erroring check must not stop that.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  // Read the OAuth callback error code on mount. The code is attacker-
  // controlled (a link to /login?error=<whatever> can be sent to the owner),
  // so it is NEVER interpolated into the DOM. We map known codes to a fixed
  // message and fall through to a generic one for anything else.
  useEffect(() => {
    const code = searchParams.get('error');
    if (code === null) {
      setError('');
      return;
    }

    setError(GOOGLE_ERROR_MAP.get(code) ?? GOOGLE_ERROR_GENERIC);
  }, [searchParams]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      await apiPost('/auth/login', { email, password });
      await refresh();
      navigate('/dashboard');
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) {
          setError(RATE_LIMIT_ERROR);
        } else {
          // Generic message regardless of the underlying cause — never
          // reveal whether the email exists.
          setError(GENERIC_ERROR);
        }
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className={styles.main} id="main">
      <div className={styles.card}>
        <h1 className={styles.title}>Ball &amp; Chaney Honey Do</h1>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
              disabled={isSubmitting}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          {error && (
            <p id="login-error" className={styles.error} role="alert" aria-live="polite">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className={styles.googleSection}>
          <div className={styles.separator} aria-hidden="true">
            <span className={styles.separatorText}>or</span>
          </div>

          <a
            href="/api/v1/auth/google/start"
            className={styles.googleButton}
            aria-label="Continue with Google"
          >
            <svg
              className={styles.googleLogo}
              width="18"
              height="18"
              viewBox="0 0 18 18"
              aria-hidden="true"
              focusable="false"
            >
              {/*
                Google's actual G mark. The brand colours are hard-coded on
                purpose — a logo is not themeable, and recolouring it to the
                app palette would breach Google's branding terms for this
                button. Inline so the strict CSP never has to fetch it.
              */}
              <path
                fill="#4285F4"
                d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.8591-3.0477.8591-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"
              />
              <path
                fill="#EA4335"
                d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z"
              />
            </svg>
            <span>Continue with Google</span>
          </a>
        </div>

        {registrationOpen && (
          <p className={styles.registerPrompt}>
            New here?{' '}
            <Link to="/register" className={styles.registerLink}>
              Create an account
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
